<template>
  <div>
    <v-simple-table v-if="invoice">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left" v-for="(header, i) in headers" :key="i">
              {{ header[lang] }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="service in services" :key="service.name">
            <td>{{ service.title[lang] || service.title.en }}</td>
            <td>{{ service.unit_value }}</td>
            <td>{{ service.quantity }}</td>
            <td>{{ Number(service.total).toFixed(2) }}</td>
            <td v-if="shouldShowBillingDetailsAction()">
              <v-tooltip color="secondary" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    color="secondary darken-3"
                    icon
                    v-on="on"
                    @click="openDetailsDialog(service)"
                  >
                    <v-icon v-text="icons.mdiTableEye"></v-icon>
                  </v-btn>
                </template>
                <span class="font-weight-bold" v-text="translations.viewMore[lang]"></span>
              </v-tooltip>
            </td>
          </tr>

          <tr>
            <td style="border-top: 1px solid black">
              {{ translations.total[lang] }} ({{ translations.beforeVat[lang] }})
            </td>
            <td style="border-top: 1px solid black"></td>
            <td style="border-top: 1px solid black"></td>
            <td style="border-top: 1px solid black">
              {{ Number(services.reduce((a, c) => a + c.total, 0)).toFixed(2) }}
            </td>
            <td v-if="shouldShowBillingDetailsAction()" style="border-top: 1px solid black"></td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
    <div v-else class="d-flex justify-center">
      <span>{{ translations.noData[lang] }}</span>
    </div>

    <v-dialog v-model="detailsDialog.show" max-width="500">
      <b-card
        v-if="detailsDialog.service"
        :title="translations.billingDetails[lang]"
        @cancel="closeDetailsDialog"
        :submitBtn="false"
      >
        <v-toolbar flat height="50">
          <div class="pl-0 ml-n4">
            <ExportLinks
              export-key="invoice-item-usages"
              :filters="{
                invoice_item_id: detailsDialog.service.invoice_item_id,
              }"
            />
          </div>
          <v-spacer></v-spacer>
        </v-toolbar>
        <b-data-table
          :headers="itemUsagesHeaders"
          :items="invoiceItems"
          style="border: none"
          :page.sync="currentPage"
          @page-sync="(p) => (currentPage = p)"
          disable-sort
          :pagination="invoiceItemsPagination"
          :hide-default-footer="!invoiceItems"
        >
          <template v-slot:item.description="{ item }">
            <span v-if="item.description && item.description[lang]">{{
              item.description[lang]
            }}</span>
            <span v-else-if="item.description">{{ item.description }}</span>
            <span v-else>-</span>
          </template>
        </b-data-table>
      </b-card>
    </v-dialog>
  </div>
</template>

<script>
import translations from '@/utils/translations/general/settings/billingDashboard';
import { isSingleDateBilling } from '@/utils/helpers';
import upperFirst from 'lodash/upperFirst';
import { mapActions, mapMutations, mapState } from 'vuex';
import ExportLinks from '@/components/general/ExportLinks.vue';

import { mdiTableEye } from '@mdi/js';

export default {
  name: 'InvoiceTable',

  mixins: [translations],

  components: {
    ExportLinks,
  },

  props: {
    invoice: {
      type: [Object, Array],
    },
    billing: {
      type: Object,
    },
  },

  data: () => ({
    timer: null,
    icons: {
      mdiTableEye,
    },
    detailsDialog: {
      show: false,
      service: null,
    },
    currentPage: 1,
  }),

  computed: {
    ...mapState('general/settings/billingDashboard/paymentHistory', [
      'invoiceItems',
      'invoiceItemsPagination',
    ]),
    lang() {
      return this.$route.params.lang;
    },
    headers() {
      const headers = [
        this.translations.headerService,
        this.translations.headerUnitValue,
        this.translations.headerQuantity,
        this.translations.headerTotal,
      ];

      if (this.shouldShowBillingDetailsAction()) {
        headers.push(this.translations.actions);
      }

      return headers;
    },
    itemUsagesHeaders() {
      return [
        {
          text: 'ID',
          value: 'id',
        },

        {
          text: this.translations.description[this.lang],
          value: 'description',
        },
      ];
    },
    services() {
      let services = [];

      for (const [key, value] of Object.entries(this.invoice)) {
        value.name = upperFirst(key.replace('_', ' '));
        value.total =
          (Number(value.unit_value || 0) - Number(value.unit_discount || 0)) *
          Number(value.quantity || 0);
        services.push(value);
      }

      return services;
    },
  },
  methods: {
    ...mapActions('general/settings/billingDashboard/paymentHistory', [
      'billingPayment',
      'getInvoiceItemUsages',
    ]),

    ...mapMutations('general/settings/billingDashboard/paymentHistory', [
      'setInvoiceItems',
      'setInvoiceItemsPagination',
    ]),
    shouldShowBillingDetailsAction() {
      return !isSingleDateBilling(this.billing);
    },
    openDetailsDialog(service) {
      this.detailsDialog.show = true;
      this.detailsDialog.service = service;
      this.getInvoiceItemUsages({
        billingId: this.billing.billing_id,
        invoiceItemId: service.invoice_item_id,
        page: 1,
      });
    },
    closeDetailsDialog() {
      this.detailsDialog.show = false;
      this.detailsDialog.service = null;
      this.currentPage = 1;
      this.setInvoiceItems([]);
      this.setInvoiceItemsPagination({ currentPage: 1 });
    },
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(val) {
        if (this.invoiceItemsPagination) {
          this.invoiceItemsPagination.current_page = val || 1;
        }
        if (this.detailsDialog.show) {
          this.getInvoiceItemUsages({
            billingId: this.billing.billing_id,
            invoiceItemId: this.detailsDialog.service.invoice_item_id,
            page: val,
          });
        }
      },
    },
    detailsDialog: {
      handler(val) {
        if (!val.show) {
          this.closeDetailsDialog();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.currentPage = this.invoiceItemsPagination?.current_page || 1;
  },
};
</script>
